import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Alert,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
} from '@mui/material';
import { ArrowBack, Save as SaveIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  listCourseTypes,
  updateCourseType,
  CourseTypeDTO,
} from '../services/courseTypeAdminService';

/**
 * System Admin: list and edit course types (display name, sort order).
 */
const CourseTypesAdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [list, setList] = useState<CourseTypeDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Record<string, { displayName: string; sortOrder: number }>>({});
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await listCourseTypes();
      setList(data);
      setEditing({});
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load';
      setError(msg);
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 403) setError('Only SYSTEM_ADMIN can access this page.');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (row: CourseTypeDTO) => {
    setEditing((prev) => ({
      ...prev,
      [row.code]: { displayName: row.displayName, sortOrder: row.sortOrder ?? 0 },
    }));
  };

  const handleSave = async (code: string) => {
    const edit = editing[code];
    if (!edit) return;
    setSaving(code);
    setError(null);
    try {
      const updated = await updateCourseType(code, {
        displayName: edit.displayName,
        sortOrder: edit.sortOrder,
      });
      setList((prev) => prev.map((r) => (r.code === code ? updated : r)));
      setEditing((prev) => {
        const next = { ...prev };
        delete next[code];
        return next;
      });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to update');
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/dashboard')} sx={{ mb: 2 }}>
        Back to Dashboard
      </Button>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Course types
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Edit display name and sort order. Guest eligibility is configured separately (Try as Guest eligibility).
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Display name</TableCell>
              <TableCell>Sort order</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map((row) => {
              const isEditing = editing[row.code] != null;
              const edit = editing[row.code];
              return (
                <TableRow key={row.code}>
                  <TableCell>{row.code}</TableCell>
                  <TableCell>
                    {isEditing && edit ? (
                      <TextField
                        size="small"
                        value={edit.displayName}
                        onChange={(e) =>
                          setEditing((prev) => ({
                            ...prev,
                            [row.code]: { ...prev[row.code]!, displayName: e.target.value },
                          }))
                        }
                        fullWidth
                      />
                    ) : (
                      row.displayName
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing && edit ? (
                      <TextField
                        size="small"
                        type="number"
                        value={edit.sortOrder}
                        onChange={(e) =>
                          setEditing((prev) => ({
                            ...prev,
                            [row.code]: {
                              ...prev[row.code]!,
                              sortOrder: parseInt(e.target.value, 10) || 0,
                            },
                          }))
                        }
                        sx={{ width: 80 }}
                      />
                    ) : (
                      row.sortOrder
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {isEditing ? (
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<SaveIcon />}
                          onClick={() => handleSave(row.code)}
                          disabled={saving === row.code}
                        >
                          Save
                        </Button>
                        <Button
                          size="small"
                          onClick={() => {
                            setEditing((prev) => {
                              const next = { ...prev };
                              delete next[row.code];
                              return next;
                            });
                          }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    ) : (
                      <Button size="small" onClick={() => startEdit(row)}>
                        Edit
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default CourseTypesAdminPage;
